import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Home, BookOpen, Trophy, Library, User } from 'lucide-react';

const BottomNav = () => {
  const location = useLocation();
  const [isDesktop, setIsDesktop] = useState(false);
  
  useEffect(() => {
    const checkDesktop = () => setIsDesktop(window.innerWidth >= 1024);
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);
  
  const navItems = [
    { to: '/', icon: Home, label: 'Today', tip: 'View your daily Scripture and prayer activities' },
    { to: '/journal', icon: BookOpen, label: 'Journal', tip: 'Save your reflections and insights' },
    { to: '/progress', icon: Trophy, label: 'Progress', tip: 'Track your 30-day retreat journey' },
    { to: '/resources', icon: Library, label: 'Resources', tip: 'Find guides, music, and spiritual tools' },
    { to: '/profile', icon: User, label: 'Profile', tip: 'Settings, cloud backup, and preferences' },
  ];

  // Desktop Sidebar Navigation
  if (isDesktop) {
    return (
      <nav className="desktop-sidebar fixed left-0 top-0 bottom-0 w-60 bg-white border-r border-[var(--border)] flex flex-col z-[9999] shadow-sm">
        {/* Logo/Header */}
        <div className="p-6 border-b border-[var(--border)]">
          <h1 className="font-heading text-xl text-[var(--primary)] font-bold">Lenten Journey</h1>
          <p className="text-sm text-[var(--text-muted)]">30-Day Ignatian Retreat</p>
        </div>
        
        {/* Nav Items */}
        <div className="flex-1 py-4">
          {navItems.map(({ to, icon: Icon, label, tip }) => {
            const isActive = location.pathname === to || 
              (to !== '/' && location.pathname.startsWith(to));
            
            return (
              <NavLink
                key={to}
                to={to}
                className={`flex items-center gap-3 px-6 py-4 transition-all ${
                  isActive 
                    ? 'text-[var(--primary-dark)] bg-[var(--primary)]/10 border-r-4 border-[var(--primary)]' 
                    : 'text-[var(--foreground)] hover:bg-[var(--muted)] hover:text-[var(--primary)]'
                }`}
                data-testid={`nav-${label.toLowerCase()}`}
              >
                <Icon className={`w-5 h-5 ${isActive ? 'stroke-[2.5]' : ''}`} />
                <span className={`text-base ${isActive ? 'font-semibold' : 'font-medium'}`}>
                  {label}
                </span>
              </NavLink>
            );
          })}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-[var(--border)] text-center">
          <p className="text-xs text-[var(--text-muted)]">Divine Mercy University</p>
        </div>
      </nav>
    );
  }

  // Mobile Bottom Navigation with Tooltips
  return (
    <nav className="mobile-bottom-nav fixed bottom-0 left-0 right-0 z-[9999] bg-white border-t border-[var(--border)] pb-safe no-print shadow-lg" data-testid="bottom-nav">
      <div className="flex items-center justify-around h-16 max-w-lg mx-auto">
        {navItems.map(({ to, icon: Icon, label, tip }) => {
          const isActive = location.pathname === to || 
            (to !== '/' && location.pathname.startsWith(to));
          
          return (
            <NavLink
              key={to}
              to={to}
              className={`nav-item relative flex flex-col items-center justify-center w-full h-full transition-colors group ${
                isActive 
                  ? 'text-[var(--primary-dark)]' 
                  : 'text-[var(--text-muted)] hover:text-[var(--primary)]'
              }`}
              data-testid={`nav-${label.toLowerCase()}`}
            >
              {/* Tooltip */}
              <span className="nav-tooltip">{tip}</span>
              
              <Icon className={`w-5 h-5 mb-1 ${isActive ? 'stroke-[2.5]' : ''}`} />
              <span className={`text-xs ${isActive ? 'font-semibold' : 'font-medium'}`}>
                {label}
              </span>
              {isActive && (
                <div className="absolute bottom-0 w-12 h-1 bg-[var(--primary)] rounded-t-full" />
              )}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNav;
